import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { IAs2Message, IAs2Partner, IAs2Stats } from '@core/models/as2-message.model';
import { getAs2MessagesThunk } from '@redux/features/as2-integration.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import {
    AnimatedListSkeleton,
    AnimatedTable,
    AnimatedTableBody,
    AnimatedTableRow,
} from 'components/animations';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import FilterInput from 'Shared/FilterInput';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import as2IntegrationService from 'services/api/as2-integration.service';
import {
    as2DirectionBadgeClass,
    as2StatusBadgeClass,
    canApproveOrReject,
    canRetry,
    formatAs2Direction,
    formatAs2Status,
    formatAs2Timestamp,
    formatFileSize,
} from './as2-status.utils';
import As2StatusGuide from './As2StatusGuide';
import As2TestingGuide from './As2TestingGuide';
import As2InboundGuide from './As2InboundGuide';
import As2EpcisDispatchPanel from './As2EpcisDispatchPanel';
import As2SectionNav from './As2SectionNav';

const STATUS_OPTIONS = [
    { value: 'ALL', label: 'All statuses' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'DISPATCHED', label: 'Dispatched' },
    { value: 'SENT', label: 'Sent' },
    { value: 'MDN_RECEIVED', label: 'MDN Received' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'AWAITING_ADMIN_INPUT', label: 'Awaiting admin review' },
    { value: 'ADMIN_REJECTED', label: 'Rejected' },
];

const DIRECTION_OPTIONS = [
    { value: 'ALL', label: 'All directions' },
    { value: 'OUT', label: 'Outbound (we send)' },
    { value: 'IN', label: 'Inbound (we receive)' },
];

function StatCard({
    label,
    sublabel,
    value,
    color,
    onClick,
    clickable,
}: {
    label: string;
    sublabel?: string;
    value: React.ReactNode;
    color: string;
    onClick?: () => void;
    clickable?: boolean;
}) {
    const Tag = clickable ? 'button' : 'div';
    return (
        <Tag
            type={clickable ? 'button' : undefined}
            onClick={onClick}
            className={`rounded-lg border p-3 text-left ${color} ${clickable ? 'cursor-pointer transition hover:opacity-90 hover:shadow-sm' : ''}`}
        >
            <p className="text-xs font-medium text-slate-600">{label}</p>
            {sublabel && <p className="text-[10px] text-slate-400">{sublabel}</p>}
            <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
        </Tag>
    );
}

const As2Messages: FC = () => {
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [directionFilter, setDirectionFilter] = useState('ALL');
    const [partnerFilter, setPartnerFilter] = useState('ALL');
    const [partners, setPartners] = useState<IAs2Partner[]>([]);
    const [stats, setStats] = useState<IAs2Stats | null>(null);
    const [retryLoading, setRetryLoading] = useState(false);
    const dispatch = useAppDispatch();
    const { messages, loading } = useAppSelector((state: RootState) => state.as2IntegrationState);

    const refresh = useCallback(() => {
        dispatch(
            getAs2MessagesThunk({
                page: page - 1,
                size: 12,
                status: statusFilter === 'ALL' ? undefined : statusFilter,
                direction: directionFilter === 'ALL' ? undefined : directionFilter,
                partner: partnerFilter === 'ALL' ? undefined : partnerFilter,
            })
        );
    }, [dispatch, page, statusFilter, directionFilter, partnerFilter]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        as2IntegrationService.listPartners().then((r: any) => {
            setPartners(r.data.as2Partners || []);
        }).catch(() => {});
        as2IntegrationService.getStats().then((r: any) => {
            setStats(r.data.stats || null);
        }).catch(() => {});
    }, []);

    const retryAll = async () => {
        setRetryLoading(true);
        try {
            await as2IntegrationService.retryAll();
            toast.success('Retry triggered for all failed messages');
            refresh();
        } catch {
            /* interceptor shows error */
        } finally {
            setRetryLoading(false);
        }
    };

    const rows: IAs2Message[] = messages?.content || [];
    const partnerOptions = [
        { value: 'ALL', label: 'All partners' },
        ...partners.map((p) => ({ value: p.code!, label: `${p.name} (${p.code})` })),
    ];

    return (
        <>
            <BreadCrumb pageTitle="AS2 Messages" />
            <section className="py-5">
                <div className="flex min-w-0 flex-col gap-4 bg-[#fff] p-3">
                    <As2SectionNav />

                    <div className="flex w-full flex-wrap items-center justify-between gap-3">
                        <div>
                            <h3 className="font-extrabold text-[#bd902e]">Message log</h3>
                            <p className="mt-1 text-sm text-slate-500">
                                Outbound EPCIS sends and inbound receipts. Filter by partner or status; open a row for
                                XML, MDN, and approve/reject.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={retryAll}
                                disabled={retryLoading}
                                className="inline-flex items-center rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                            >
                                {retryLoading ? 'Retrying…' : 'Retry all failed'}
                            </button>
                        </div>
                    </div>

                    <As2EpcisDispatchPanel partners={partners} environmentFilter="ALL" compact />

                    <As2InboundGuide />
                    <As2StatusGuide />

                    {/* Stats dashboard — 4 primary cards (click Failed / Awaiting to filter) */}
                    {stats && (
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <StatCard
                                label="Sent today"
                                sublabel="outbound"
                                value={stats.totalOutToday}
                                color="border-slate-200 bg-slate-50"
                            />
                            <StatCard
                                label="MDN success"
                                sublabel="confirmed"
                                value={`${stats.mdnSuccessRate.toFixed(1)}%`}
                                color={
                                    stats.mdnSuccessRate >= 90
                                        ? 'border-green-100 bg-green-50'
                                        : stats.mdnSuccessRate >= 70
                                        ? 'border-amber-100 bg-amber-50'
                                        : 'border-red-100 bg-red-50'
                                }
                            />
                            <StatCard
                                label="Failed"
                                sublabel="needs retry"
                                value={stats.failedCount}
                                color="border-red-100 bg-red-50"
                                clickable
                                onClick={() => {
                                    setStatusFilter('FAILED');
                                    setDirectionFilter('OUT');
                                    setPage(1);
                                }}
                            />
                            <StatCard
                                label="Awaiting"
                                sublabel="inbound review"
                                value={stats.awaitingAdminCount}
                                color="border-amber-100 bg-amber-50"
                                clickable
                                onClick={() => {
                                    setStatusFilter('AWAITING_ADMIN_INPUT');
                                    setDirectionFilter('IN');
                                    setPage(1);
                                }}
                            />
                        </div>
                    )}

                    {/* Secondary stats row */}
                    {stats && (
                        <div className="flex flex-wrap items-center gap-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                            <span>Inbound today: <strong>{stats.totalInToday}</strong></span>
                            <span>MDN received: <strong>{stats.mdnReceivedCount}</strong></span>
                            <span>In-flight: <strong>{stats.pendingCount}</strong></span>
                            {stats.perPartner && stats.perPartner.length > 0 && (
                                <span className="text-slate-500">
                                    {stats.perPartner.map((p) => `${p.partnerCode}: ${p.mdnReceived}/${p.sent + p.mdnReceived + p.failed}`).join(' · ')}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Partner chips */}
                    {partners.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {partners.map((p) => (
                                <span
                                    key={p.code}
                                    onClick={() => {
                                        setPartnerFilter(p.code === partnerFilter ? 'ALL' : p.code!);
                                        setPage(1);
                                    }}
                                    className={`cursor-pointer rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${
                                        partnerFilter === p.code
                                            ? 'border-[#bd902e] bg-[#bd902e] text-white'
                                            : 'border-slate-200 bg-white text-slate-700 hover:border-[#bd902e]'
                                    } ${p.enabled === false ? 'opacity-50' : ''}`}
                                >
                                    {p.code}
                                    {p.enabled === false && ' (disabled)'}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Filters */}
                    <div className="flex flex-wrap gap-4">
                        <div className="min-w-[180px] flex-1">
                            <label className="mb-1.5 block text-sm font-medium text-slate-700">Status</label>
                            <FilterInput
                                id="as2-status"
                                value={statusFilter}
                                onChange={(v) => { setStatusFilter(v); setPage(1); }}
                                options={STATUS_OPTIONS}
                                placeholder="All"
                                optionLabelKey="label"
                                optionValueKey="value"
                            />
                        </div>
                        <div className="min-w-[180px] flex-1">
                            <label className="mb-1.5 block text-sm font-medium text-slate-700">Direction</label>
                            <FilterInput
                                id="as2-direction"
                                value={directionFilter}
                                onChange={(v) => { setDirectionFilter(v); setPage(1); }}
                                options={DIRECTION_OPTIONS}
                                placeholder="All"
                                optionLabelKey="label"
                                optionValueKey="value"
                            />
                        </div>
                        {partners.length > 1 && (
                            <div className="min-w-[180px] flex-1">
                                <label className="mb-1.5 block text-sm font-medium text-slate-700">Partner</label>
                                <FilterInput
                                    id="as2-partner"
                                    value={partnerFilter}
                                    onChange={(v) => { setPartnerFilter(v); setPage(1); }}
                                    options={partnerOptions}
                                    placeholder="All"
                                    optionLabelKey="label"
                                    optionValueKey="value"
                                />
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div className="w-full min-w-0 overflow-x-auto">
                                <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                    <thead className="ltr:text-left rtl:text-right">
                                        <tr className="rounded-md bg-slate-50">
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">ID</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Partner</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Direction</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Status</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">MDN</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">EPCIS</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Size</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Created</th>
                                            <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Actions</th>
                                        </tr>
                                    </thead>
                                    <AnimatedTableBody
                                        loading={loading}
                                        itemCount={rows.length}
                                        animationKey={`as2-${page}-${statusFilter}-${directionFilter}-${partnerFilter}`}
                                    >
                                        {rows.map((row) => (
                                            <AnimatedTableRow
                                                key={row.id}
                                                rowKey={row.id}
                                                detailRecord={row}
                                                className="relative rounded-md bg-slate-50 hover:bg-[#bd902e10]"
                                            >
                                                <td className="px-3.5 py-2.5 text-sm">{row.id}</td>
                                                <td className="px-3.5 py-2.5 text-sm font-medium">{row.partner || '—'}</td>
                                                <td className="px-3.5 py-2.5">
                                                    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${as2DirectionBadgeClass(row.direction)}`}>
                                                        {formatAs2Direction(row.direction)}
                                                    </span>
                                                </td>
                                                <td className="px-3.5 py-2.5">
                                                    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${as2StatusBadgeClass(row.status)}`}>
                                                        {formatAs2Status(row.status)}
                                                    </span>
                                                </td>
                                                <td className="px-3.5 py-2.5 text-xs text-slate-600">{row.mdnStatus || '—'}</td>
                                                <td className="px-3.5 py-2.5 text-xs text-slate-500">{row.epcisId || '—'}</td>
                                                <td className="px-3.5 py-2.5 text-xs text-slate-500">{formatFileSize(row.fileSizeBytes)}</td>
                                                <td className="px-3.5 py-2.5 text-xs text-slate-500">{formatAs2Timestamp(row.createdAt)}</td>
                                                <td className="px-3.5 py-2.5">
                                                    {canApproveOrReject(row) || canRetry(row) || row.status === 'DISPATCHED' || row.status === 'SENT' ? (
                                                        <Link
                                                            to={`${PRIVATEPATHS.As2Messages}/${row.id}`}
                                                            className={`text-sm font-semibold hover:underline ${
                                                                canApproveOrReject(row) ? 'text-amber-700' :
                                                                canRetry(row) ? 'text-red-600' : 'text-[#bd902e]'
                                                            }`}
                                                        >
                                                            {canApproveOrReject(row) ? 'Review' : canRetry(row) ? 'Retry' : 'Detail'}
                                                        </Link>
                                                    ) : row.status === 'MDN_RECEIVED' ? (
                                                        <Link
                                                            to={`${PRIVATEPATHS.As2Messages}/${row.id}`}
                                                            className="text-xs text-slate-500 hover:underline"
                                                        >
                                                            View
                                                        </Link>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">—</span>
                                                    )}
                                                </td>
                                            </AnimatedTableRow>
                                        ))}
                                    </AnimatedTableBody>
                                </AnimatedTable>
                            </div>
                            <Pagination
                                perPageData={messages?.pageable?.pageSize || 12}
                                total={messages?.totalElements || 0}
                                currentPage={page}
                                changePage={(p) => setPage(Number(p))}
                                length={messages?.numberOfElements || rows.length}
                            />
                        </>
                    )}

                    <As2TestingGuide />
                </div>
            </section>
        </>
    );
};

export default As2Messages;
